/**
 * Landing Page Theme - Light Modern Editorial
 *
 * A sophisticated light theme for the marketing landing page.
 * Features: Clean typography, refined spacing, subtle gradients, premium feel.
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';

// =============================================================================
// COLOR PALETTE - Light Modern
// =============================================================================

export const landingColors = {
  // Primary - Indigo (consistent with app)
  primary: '#6366F1',
  primaryLight: '#818CF8',
  primaryDark: '#4F46E5',
  primaryMuted: 'rgba(99, 102, 241, 0.08)',

  // Secondary - Violet accent
  secondary: '#8B5CF6',
  secondaryLight: '#A78BFA',

  // Backgrounds
  background: '#FAFBFC',
  backgroundAlt: '#F1F5F9',
  surface: '#FFFFFF',
  surfaceElevated: '#FFFFFF',

  // Text
  textPrimary: '#0F172A',
  textSecondary: '#475569',
  textMuted: '#94A3B8',
  textOnPrimary: '#FFFFFF',

  // Borders
  border: '#E2E8F0',
  borderLight: '#F1F5F9',

  // Semantic
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#3B82F6',

  // Special
  highlight: '#FEF3C7',
  accent: '#EC4899',
};

// =============================================================================
// TYPOGRAPHY - Editorial Style
// =============================================================================

const fontFamilies = {
  // Display: Bold, distinctive headings
  display: '"Cabinet Grotesk", "Satoshi", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
  // Body: Clean, readable
  body: '"General Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
  // Mono: For badges, numbers
  mono: '"JetBrains Mono", "Fira Code", monospace',
};

// =============================================================================
// THEME CONFIGURATION
// =============================================================================

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: landingColors.primary,
      light: landingColors.primaryLight,
      dark: landingColors.primaryDark,
      contrastText: landingColors.textOnPrimary,
    },
    secondary: {
      main: landingColors.secondary,
      light: landingColors.secondaryLight,
      contrastText: landingColors.textOnPrimary,
    },
    background: {
      default: landingColors.background,
      paper: landingColors.surface,
    },
    text: {
      primary: landingColors.textPrimary,
      secondary: landingColors.textSecondary,
      disabled: landingColors.textMuted,
    },
    divider: landingColors.border,
    success: {
      main: landingColors.success,
    },
    warning: {
      main: landingColors.warning,
    },
    error: {
      main: landingColors.error,
    },
    info: {
      main: landingColors.info,
    },
  },

  typography: {
    fontFamily: fontFamilies.body,

    h1: {
      fontFamily: fontFamilies.display,
      fontWeight: 700,
      fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
      lineHeight: 1.1,
      letterSpacing: '-0.03em',
      color: landingColors.textPrimary,
    },
    h2: {
      fontFamily: fontFamilies.display,
      fontWeight: 700,
      fontSize: 'clamp(2rem, 4vw, 3rem)',
      lineHeight: 1.15,
      letterSpacing: '-0.02em',
      color: landingColors.textPrimary,
    },
    h3: {
      fontFamily: fontFamilies.display,
      fontWeight: 600,
      fontSize: 'clamp(1.5rem, 3vw, 2rem)',
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
      color: landingColors.textPrimary,
    },
    h4: {
      fontFamily: fontFamilies.display,
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.3,
      color: landingColors.textPrimary,
    },
    h5: {
      fontFamily: fontFamilies.display,
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
      color: landingColors.textPrimary,
    },
    h6: {
      fontFamily: fontFamilies.display,
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
      color: landingColors.textPrimary,
    },
    subtitle1: {
      fontFamily: fontFamilies.body,
      fontSize: '1.125rem',
      lineHeight: 1.6,
      color: landingColors.textSecondary,
    },
    subtitle2: {
      fontFamily: fontFamilies.body,
      fontSize: '1rem',
      lineHeight: 1.5,
      fontWeight: 500,
      color: landingColors.textSecondary,
    },
    body1: {
      fontFamily: fontFamilies.body,
      fontSize: '1rem',
      lineHeight: 1.7,
      color: landingColors.textSecondary,
    },
    body2: {
      fontFamily: fontFamilies.body,
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: landingColors.textSecondary,
    },
    button: {
      fontFamily: fontFamilies.body,
      fontWeight: 600,
      fontSize: '0.9375rem',
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
    caption: {
      fontFamily: fontFamilies.mono,
      fontSize: '0.75rem',
      letterSpacing: '0.02em',
      textTransform: 'uppercase',
      color: landingColors.textMuted,
    },
    overline: {
      fontFamily: fontFamilies.mono,
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: landingColors.primary,
    },
  },

  shape: {
    borderRadius: 12,
  },

  shadows: [
    'none',
    '0 1px 2px rgba(15, 23, 42, 0.04)',
    '0 2px 4px rgba(15, 23, 42, 0.04)',
    '0 4px 8px rgba(15, 23, 42, 0.04)',
    '0 6px 12px rgba(15, 23, 42, 0.05)',
    '0 8px 16px rgba(15, 23, 42, 0.05)',
    '0 12px 24px rgba(15, 23, 42, 0.06)',
    '0 16px 32px rgba(15, 23, 42, 0.06)',
    '0 20px 40px rgba(15, 23, 42, 0.07)',
    '0 24px 48px rgba(15, 23, 42, 0.08)',
    '0 32px 64px rgba(15, 23, 42, 0.08)',
    '0 40px 80px rgba(15, 23, 42, 0.09)',
    '0 48px 96px rgba(15, 23, 42, 0.1)',
    '0 56px 112px rgba(15, 23, 42, 0.1)',
    '0 64px 128px rgba(15, 23, 42, 0.1)',
    '0 72px 144px rgba(15, 23, 42, 0.1)',
    '0 80px 160px rgba(15, 23, 42, 0.1)',
    '0 88px 176px rgba(15, 23, 42, 0.1)',
    '0 96px 192px rgba(15, 23, 42, 0.1)',
    '0 104px 208px rgba(15, 23, 42, 0.1)',
    '0 112px 224px rgba(15, 23, 42, 0.1)',
    '0 120px 240px rgba(15, 23, 42, 0.1)',
    '0 128px 256px rgba(15, 23, 42, 0.1)',
    '0 136px 272px rgba(15, 23, 42, 0.1)',
    '0 144px 288px rgba(15, 23, 42, 0.1)',
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        @font-face {
          font-family: 'Cabinet Grotesk';
          src: url('https://fonts.cdnfonts.com/css/cabinet-grotesk');
          font-display: swap;
        }

        @font-face {
          font-family: 'General Sans';
          src: url('https://fonts.cdnfonts.com/css/general-sans');
          font-display: swap;
        }

        html {
          scroll-behavior: smooth;
        }

        body {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        ::selection {
          background: ${landingColors.primaryMuted};
          color: ${landingColors.primary};
        }
      `,
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '12px 24px',
          boxShadow: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: 'none',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
        },
        contained: {
          '&.MuiButton-containedPrimary': {
            background: `linear-gradient(135deg, ${landingColors.primary} 0%, ${landingColors.primaryDark} 100%)`,
            '&:hover': {
              background: `linear-gradient(135deg, ${landingColors.primaryLight} 0%, ${landingColors.primary} 100%)`,
            },
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            backgroundColor: landingColors.primaryMuted,
          },
        },
        sizeLarge: {
          padding: '16px 32px',
          fontSize: '1rem',
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${landingColors.border}`,
          boxShadow: '0 4px 24px rgba(15, 23, 42, 0.04)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 12px 48px rgba(15, 23, 42, 0.08)',
            transform: 'translateY(-4px)',
          },
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            '& fieldset': {
              borderColor: landingColors.border,
              borderWidth: 2,
            },
            '&:hover fieldset': {
              borderColor: landingColors.primary,
            },
            '&.Mui-focused fieldset': {
              borderColor: landingColors.primary,
              borderWidth: 2,
            },
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          fontSize: '0.75rem',
          letterSpacing: '0.02em',
        },
        filled: {
          backgroundColor: landingColors.primaryMuted,
          color: landingColors.primary,
        },
      },
    },
  },
};

export const landingTheme = createTheme(themeOptions);

// =============================================================================
// CSS VARIABLES FOR CUSTOM STYLING
// =============================================================================

export const cssVariables = {
  '--landing-primary': landingColors.primary,
  '--landing-primary-light': landingColors.primaryLight,
  '--landing-primary-dark': landingColors.primaryDark,
  '--landing-primary-muted': landingColors.primaryMuted,
  '--landing-secondary': landingColors.secondary,
  '--landing-bg': landingColors.background,
  '--landing-bg-alt': landingColors.backgroundAlt,
  '--landing-surface': landingColors.surface,
  '--landing-text-primary': landingColors.textPrimary,
  '--landing-text-secondary': landingColors.textSecondary,
  '--landing-text-muted': landingColors.textMuted,
  '--landing-border': landingColors.border,
  '--landing-accent': landingColors.accent,
};

export default landingTheme;
